import { type ReactNode } from 'react';
import { Banner, Kbd } from '../components/ui';
import type { Workspace } from '../lib/types';
import { useHotkeys } from '../lib/useHotkeys';
import { SourceBar, TransportBar } from './RecorderControls';
import { RecorderStage } from './RecorderStage';
import type { UploadTransport } from './uploader';
import { useRecorder, type RecorderApi } from './useRecorder';

/**
 * The recorder, assembled. Both /new and /r/:token mount this same component;
 * they differ only in the chrome around it and in what the preview screen says
 * once the take is done.
 */

export interface RecorderProps {
  transport: UploadTransport;
  makeTitle: () => string;
  workspace?: Workspace;
  orgId?: string | null;
  /** Rendered instead of the stage once a take is finished. */
  renderPreview: (recorder: RecorderApi) => ReactNode;
  /** Shown when the browser cannot capture a screen at all. */
  renderUnsupported?: () => ReactNode;
  /** Extra content under the source bar (destination pickers, name fields). */
  children?: ReactNode;
  onCancel?: () => void;
}

export function Recorder({
  transport,
  makeTitle,
  workspace,
  orgId,
  renderPreview,
  renderUnsupported,
  children,
  onCancel,
}: RecorderProps) {
  const recorder = useRecorder({ transport, makeTitle, workspace, orgId });
  const { phase } = recorder;

  useHotkeys(
    {
      space: () => {
        if (phase === 'ready') recorder.start();
        else if (phase === 'countdown') recorder.skipCountdown();
        else recorder.togglePause();
      },
      enter: () => {
        if (phase === 'recording' || phase === 'paused') void recorder.stop();
      },
      'shift+r': () => recorder.restart(),
      escape: () => {
        if (phase === 'countdown') recorder.skipCountdown();
      },
    },
    phase !== 'preview' && phase !== 'unsupported',
  );

  if (phase === 'unsupported') {
    return (
      <div className="space-y-4">
        <Banner tone="caution" title="This browser cannot record a screen">
          Screen capture needs Chrome, Edge, Firefox, or Safari 16+ on a desktop — and a page
          served over HTTPS. Phones cannot capture their own screen from a browser at all; that
          is an OS limitation, not a Slate one.
        </Banner>
        {renderUnsupported?.()}
      </div>
    );
  }

  if (phase === 'preview') return <>{renderPreview(recorder)}</>;

  return (
    <div className="space-y-5">
      <RecorderStage recorder={recorder} />

      {recorder.error ? (
        <Banner tone="danger" title="Capture problem">
          {recorder.error}
        </Banner>
      ) : null}

      {recorder.upload.error && (phase === 'recording' || phase === 'paused') ? (
        <Banner tone="caution" title="The upload is struggling">
          {recorder.upload.error} Keep recording — parts are retried, and anything still
          outstanding is flushed when you finish.
        </Banner>
      ) : null}

      <SourceBar recorder={recorder} />

      {children}

      <div className="rounded-md border border-hairline bg-slate-850 p-4">
        <TransportBar recorder={recorder} onCancel={onCancel} />
      </div>

      {phase === 'idle' || phase === 'ready' ? (
        <p className="flex flex-wrap items-center gap-1.5 text-caption text-chalk-faint">
          Once you are rolling: <Kbd>Space</Kbd> pauses and resumes,{' '}
          <Kbd>Shift</Kbd>
          <Kbd>R</Kbd> throws the take away and starts again without re-picking your screen, and{' '}
          <Kbd>Enter</Kbd> finishes.
          {recorder.codec ? (
            <span className="ml-1 text-chalk-faint/70">Recording as {recorder.codec.label}.</span>
          ) : null}
        </p>
      ) : null}
    </div>
  );
}
